import React, { useState } from 'react';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { useAuth } from '../context/AuthContext';
import { orderBySpeed } from '../utils/planetSpeedOrder';
import { addAreaHighlightOverlaysAndMessages } from '../components/addAreaHighlightOverlaysUtil';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  OutlinedInput,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import HelpIcon from './HelpIcon';

/**
 * Width of the label column, shared by every row of the form.
 *
 * One constant rather than a per-row number so the dropdowns, the degree inputs and the two radio
 * groups all begin at the same x. Wide enough for the longest label, "Display Style"; the rows
 * previously used 48, which left the radio groups starting well right of the fields above them.
 */
const LABEL_COLUMN_WIDTH = 104;

const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter',
  'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu'
];

const COLOR_OPTIONS = [
  'rgba(229, 57, 53, 0.55)',    // Red
  'rgba(30, 136, 229, 0.55)',   // Blue
  'rgba(67, 160, 71, 0.55)',    // Green
  'rgba(251, 192, 45, 0.55)',   // Yellow
  'rgba(142, 36, 170, 0.55)',   // Purple
  'rgba(255, 87, 34, 0.55)',    // Orange
  'rgba(120, 144, 156, 0.55)',  // Gray
  'rgba(255, 193, 7, 0.55)',    // Amber
  'rgba(233, 30, 99, 0.55)',    // Pink
  'rgba(0, 188, 212, 0.55)',    // Cyan
  'rgba(205, 220, 57, 0.55)',   // Lime
  'rgba(96, 125, 139, 0.55)',   // Blue Gray
  'rgba(255, 152, 0, 0.55)',    // Deep Orange
  'rgba(124, 179, 66, 0.55)',   // Light Green
  'rgba(255, 255, 255, 0.55)',  // White
  'rgba(0, 0, 0, 0.55)',        // Black
  'rgba(255, 234, 0, 0.55)',    // Bright Yellow
  'rgba(0, 200, 83, 0.55)',     // Vivid Green
  'rgba(213, 0, 249, 0.55)',    // Vivid Purple
  'rgba(255, 61, 0, 0.55)',     // Vivid Red
];


import PropTypes from 'prop-types';

function AspectsAreaHighlightDialog({ open, onClose, onRun, barData, extendedBarData, onJournalAppend }) {
  const { api, isAdmin } = useAuth();
  const { addOverlay } = useChartOverlays();
  // State for planet/aspect rows (must be declared first!)
  // Ordered through the same rule the dropdowns enforce. The defaults were Sun/Rahu, and Rahu is
  // the slower body, so the dialog would otherwise OPEN violating the invariant and stay that way
  // until the user happened to touch a dropdown — which is also the state it would run in.
  const [rows, setRows] = useState(() => {
    const { planet1, planet2 } = orderBySpeed('Sun', 'Rahu');
    return [
      { planet1, planet2, from: 30 },
      { planet1, planet2, from: 210 },
    ];
  });

  // 'same'      row 2 mirrors row 1's bodies; only the closing degree is the user's to choose.
  // 'different' row 2 names its own pair, so the window opens on one aspect and closes on another.
  //
  // Default is 'same', which is exactly what the dialog could express before, so an existing user
  // finds the same study they had.
  const [pairMode, setPairMode] = useState('same');
  const isDifferentPair = pairMode === 'different';

  // 'bands' keeps the shaded vertical highlight; 'lines' replaces it with one line per window,
  // drawn from the opening bar's open to the closing bar's close. The report, the tables and every
  // statistic are computed from the date pairs and are untouched by this — it reaches the overlays
  // and nothing else.
  const [displayStyle, setDisplayStyle] = useState('bands');

  const handlePairModeChange = (mode) => {
    setPairMode(mode);
    if (mode === 'same') {
      // Snap row 2 back onto row 1. Leaving the independent selection in place while the dropdowns
      // are disabled would show a pair the study is not going to use — the state the disabled
      // controls describe has to be the state that runs.
      setRows(prev => prev.map((row, i) =>
        i === 1 ? { ...row, planet1: prev[0].planet1, planet2: prev[0].planet2 } : row));
    }
  };

  // Map planet names to color indices for default color selection
  const PLANET_COLOR_MAP = {
    'Sun': COLOR_OPTIONS[0],
    'Moon': COLOR_OPTIONS[1],
    'Mercury': COLOR_OPTIONS[2],
    'Venus': COLOR_OPTIONS[3],
    'Mars': COLOR_OPTIONS[4],
    'Jupiter': COLOR_OPTIONS[5],
    'Saturn': COLOR_OPTIONS[6],
    'Uranus': COLOR_OPTIONS[7],
    'Neptune': COLOR_OPTIONS[8],
    'Pluto': COLOR_OPTIONS[9],
    'Rahu': COLOR_OPTIONS[10]
  };

  const [color, setColor] = useState(PLANET_COLOR_MAP['Rahu']);

  // When planet2 in the first row changes, update color to match
  React.useEffect(() => {
    setColor(PLANET_COLOR_MAP[rows[0].planet2] || COLOR_OPTIONS[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows[0].planet2]);

  const handleRowChange = (idx, field, value) => {
    setRows(prev => {
      // Editing the opening degree pre-fills the closing one with its opposition — but only when
      // the two rows describe one pair. With independent pairs the closing degree is a separate
      // event the user chose deliberately, and silently rewriting it because they went back and
      // adjusted the opening degree would discard that choice.
      if (idx === 0 && field === 'from' && !isDifferentPair) {
        let num = parseFloat(value);
        if (isNaN(num)) num = '';
        const toVal = num === '' ? '' : ((num + 180) % 360);
        return prev.map((row, i) => {
          if (i === 0) return { ...row, from: num };
          if (i === 1) return { ...row, from: toVal };
          return row;
        });
      }

      if (field === 'planet1' || field === 'planet2') {
        // Slower body becomes Planet 1, whichever dropdown the user filled. An aspect is read
        // with the slower body as the anchor, and normalising here rather than at submit time
        // means the dropdowns visibly show the pair as it will actually be studied — a silent
        // swap on submit would leave the UI disagreeing with the label on the chart.
        //
        // Applied to whichever row was edited, so the second pair is anchored the same way as the
        // first. A rule that held for one row and not the other would make the same two bodies
        // read differently depending on which end of the window they bounded.
        const candidate = { ...prev[idx], [field]: value };
        const ordered = orderBySpeed(candidate.planet1, candidate.planet2);
        const next = { ...candidate, planet1: ordered.planet1, planet2: ordered.planet2 };

        return prev.map((row, i) => {
          if (i === idx) return next;
          // Row 2 mirrors row 1 only while the pairs are meant to be the same.
          if (idx === 0 && i === 1 && !isDifferentPair) {
            return { ...row, planet1: next.planet1, planet2: next.planet2 };
          }
          return row;
        });
      }

      return prev.map((row, i) => i === idx ? { ...row, [field]: value } : row);
    });
  };



  const PLANET_INDEX_MAP = {
    'Sun': 0,
    'Moon': 1,
    'Mercury': 2,
    'Venus': 3,
    'Mars': 4,
    'Jupiter': 5,
    'Saturn': 6,
    'Uranus': 7,
    'Neptune': 8,
    'Pluto': 9,
    'Rahu': 10,
    'North Node': 10
  };

  const handleRun = async () => {

  // Diagnostic: Log barData and rows before running
  console.log('[AspectsAreaHighlightDialog] handleRun called. barData:', barData);
  console.log('[AspectsAreaHighlightDialog] handleRun called. rows:', rows);
  // The bodies the study will actually run on. In same-pair mode row 2's dropdowns are disabled,
  // so its stored planets are a mirror rather than a choice — read them from row 1 so the payload
  // cannot disagree with what the dialog is showing.
  const startPlanets = { planet1: rows[0].planet1, planet2: rows[0].planet2 };
  const endPlanets = isDifferentPair
    ? { planet1: rows[1].planet1, planet2: rows[1].planet2 }
    : startPlanets;

  // Two shapes, on purpose.
  //
  // startPair/endPair is the current contract: bodies by name, one object per end of the window.
  // planet11..degrees2 repeat the same thing as indices, which is what the backend read before.
  //
  // The duplication is deliberate and cheap. The frontend ships to Firebase Hosting and the
  // backend to Cloud Run, independently, so there is always a window where one is newer than the
  // other. A payload carrying only the new shape would reach an old backend as planet index 0 for
  // everything — that is the Sun, so the study would return a plausible set of bands for bodies
  // nobody selected, with no error to notice. Sending both makes the deploy order irrelevant.
  // Drop the flat fields once the backend has been on this version for a while.
  const payload = {
    "isDifferentPair": isDifferentPair,
    "startPair": { ...startPlanets, "degree": Number(rows[0].from) },
    "endPair": { ...endPlanets, "degree": Number(rows[1].from) },
    "overlayColor": color,

    "planet11": PLANET_INDEX_MAP[startPlanets.planet1],
    "planet12": PLANET_INDEX_MAP[startPlanets.planet2],
    "degrees1": Number(rows[0].from),
    "planet21": PLANET_INDEX_MAP[endPlanets.planet1],
    "planet22": PLANET_INDEX_MAP[endPlanets.planet2],
    "degrees2": Number(rows[1].from),

    "startDateTime": (extendedBarData?.dates?.[0] || barData.dates[0]),
    "endDateTime": (extendedBarData?.dates?.[extendedBarData.dates.length - 1] || barData.dates[barData.dates.length - 1]),
    "symbol": barData.symbol,
    "color": color
  };
  console.log('[AspectsAreaHighlightDialog] Payload for backend:', payload);

  try {
    const res = await api.post('/api/area-highlight-study/planet-to-planet-aspect', payload);
    const data = res.data;
    console.log('[AspectsAreaHighlightDialog] Backend response:', data);
    // Expect: {datePairs: [...], summaryLines: [...], futureDates: [...]}
    const datePairs = Array.isArray(data) ? data : data?.datePairs || [];
    const summaryLines = data?.summaryLines || [];
    const futureDates = data?.futureDates || [];
    console.log('[AspectsAreaHighlightDialog] Passing to overlays/messages:', { datePairs, summaryLines, futureDates });
    addAreaHighlightOverlaysAndMessages({
        isAdmin,
      datePairs: datePairs || [],
      summaryLines: summaryLines, // Pass the raw data, the utility will handle slicing
      futureDates: futureDates || [],
      params: {
        color,
        displayStyle,
        // Names both ends when they differ, so two studies that share an opening pair but close on
        // different ones are still tellable apart on the chart and in the journal.
        label: isDifferentPair
          ? `${startPlanets.planet1}-${startPlanets.planet2} ${rows[0].from}° → ${endPlanets.planet1}-${endPlanets.planet2} ${rows[1].from}°`
          : `${startPlanets.planet1}-${startPlanets.planet2} ${rows[0].from}°/${rows[1].from}°`,
        planet1: startPlanets.planet1,
        planet2: startPlanets.planet2,
        degrees: rows[0].from
      },
      addOverlay,
      barData,
      extendedBarData,
      updateMessagesPanel: ({ summaryLines: processedSummary, futureDates }) => {
        if (typeof onRun === 'function') {
          const title = isDifferentPair
            ? `${startPlanets.planet1}-${startPlanets.planet2}-${rows[0].from} → ${endPlanets.planet1}-${endPlanets.planet2}-${rows[1].from}`
            : `${startPlanets.planet1}-${startPlanets.planet2}-${rows[0].from}-${rows[1].from}`;
          onRun({ stats: processedSummary, futureDates, title });
        }
      },
      onJournalAppend,
    });
  } catch (err) {
    console.error('[AspectsAreaHighlightDialog] handleRun error:', err);
  }
};

  return (
    // maxHeight raised from 380: the Planet Pair row is new, and the sign controls it replaced cost
    // width rather than height, so the old ceiling would have clipped the RUN button.
    <Dialog open={open} onClose={onClose} PaperProps={{ style: { minWidth: 900, borderRadius: 12, maxHeight: 440, padding: 0 } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: '1.3rem', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Aspect Area Highlight Studies</span>
        <HelpIcon helpKey="aspectsAreaHighlightDialog" />
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: 1.5, paddingTop: 1.5 }}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 500, mb: 0.5 }}>Select Overlay Color:</Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={color}
              onChange={e => setColor(e.target.value)}
              renderValue={selected => (
                <Box display="flex" alignItems="center">
                  <span style={{ display: 'inline-block', width: 24, height: 24, borderRadius: 4, backgroundColor: selected, marginRight: 10, border: '1px solid #bbb' }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{selected}</span>
                </Box>
              )}
            >
              {COLOR_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>
                  <Box display="flex" alignItems="center">
                    <span style={{ display: 'inline-block', width: 24, height: 24, borderRadius: 4, backgroundColor: opt, marginRight: 10, border: '1px solid #bbb' }} />
                    <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{opt}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Grid container spacing={2}>
          {rows.map((row, idx) => {
            // Prevent same planet selection in a row
            const planet1Options = PLANETS;
            const planet2Options = PLANETS.filter(p => p !== row.planet1);
            const label = idx === 0 ? 'From' : 'To';
            return (
              <Grid item xs={12} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Planet</Typography>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={row.planet1}
                      onChange={e => handleRowChange(idx, 'planet1', e.target.value)}
                      disabled={idx === 1 && !isDifferentPair}
                    >
                      {planet1Options.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontWeight: 500, minWidth: 48, ml: 1 }}>Planet</Typography>
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <Select
                      value={row.planet2}
                      onChange={e => handleRowChange(idx, 'planet2', e.target.value)}
                      disabled={idx === 1 && !isDifferentPair}
                    >
                      {planet2Options.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <Typography sx={{ fontWeight: 500, minWidth: 32, ml: 2 }}>{label}</Typography>
                  <OutlinedInput
                    type="number"
                    value={row.from}
                    onChange={e => {
                      let val = e.target.value;
                      // Only allow valid float numbers
                      if (/^\d*\.?\d{0,2}$/.test(val) || val === '') {
                        let num = parseFloat(val);
                        if (isNaN(num)) num = '';
                        else if (num < 0) num = 0;
                        else if (num > 359.99) num = 359.99;
                        handleRowChange(idx, 'from', num === '' ? '' : num);
                      }
                    }}
                    sx={{ width: 100, ml: 0.5, height: 40, fontSize: 16, '& input': { textAlign: 'center', padding: '10px 8px' } }}
                    inputProps={{ min: 0, max: 359.99, step: 0.01, inputMode: 'decimal', pattern: '[0-9]*' }}
                  />
                </Box>
              </Grid>
            );
          })}

          {/*
            Planet Pair — decides whether the window's two ends share a pair of bodies.

            Placed below both rows because it governs the relationship between them rather than
            belonging to either, and because reading it after the rows matches what it says: these
            two pairs are the same, or they are not.
          */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Planet Pair</Typography>
              <RadioGroup
                row
                value={pairMode}
                onChange={e => handlePairModeChange(e.target.value)}
              >
                <FormControlLabel value="same" control={<Radio size="small" />} label="Same" />
                <FormControlLabel value="different" control={<Radio size="small" />} label="Different" />
              </RadioGroup>
            </Box>
          </Grid>

          {/*
            Display Style — how the matched windows are drawn, and nothing else.

            Sits on its own row under Planet Pair, sharing the same label column, so the two radio
            groups and the fields above them all start at the same x.
          */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 500, minWidth: LABEL_COLUMN_WIDTH }}>Display Style</Typography>
              <RadioGroup row value={displayStyle} onChange={e => setDisplayStyle(e.target.value)}>
                <FormControlLabel value="bands" control={<Radio size="small" />} label="Vertical Bands" />
                <FormControlLabel value="lines" control={<Radio size="small" />} label="Lines Only" />
              </RadioGroup>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
          <Button
            variant="contained"
            color="error"
            sx={{ fontWeight: 600, borderRadius: 2, boxShadow: 'none', minWidth: 90 }}
            onClick={onClose}
          >
            CLOSE
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ px: 4, fontWeight: 'bold', fontSize: 15 }}
            onClick={handleRun}
          >
            RUN
          </Button>
      </DialogActions>
    </Dialog>
  );
}

AspectsAreaHighlightDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRun: PropTypes.func,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string).isRequired,
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired,
    symbol: PropTypes.string,
  }).isRequired,
  onJournalAppend: PropTypes.func,
};

export default AspectsAreaHighlightDialog;
